import { useState, useEffect } from "react";

// New unified API (project 01b0efcc = grotron-api NEW_API_1). Replaces the dead
// us-east-1 Lambda. fm_zoom_details reads the CONFIGURED meeting from fm_gt_config
// and returns the meeting + public price info; fm_zoom_register handles free/coupon.
const API_BASE =
  "https://api.grotron.farm/01b0efcc-69b3-4b7b-9802-8a6201f93462";
// Unified payment platform (project 4a46bf2c) — used for PAID registrations.
const PAY_API =
  "https://api.grotron.farm/4a46bf2c-1ce8-4fa5-b746-4f4779c2bb9a";

export default function MeetingRegistration() {
  const [meeting, setMeeting] = useState(null);
  const [price, setPrice] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchMeeting = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/fm_zoom_details`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();

        if (!data || data.error || !data.topic || !data.start_time) {
          setMeeting("NO_MEETING");
        } else {
          setMeeting(data);
          setPrice(data.price || null);
        }
      } catch (err) {
        console.error(err);
        setMeeting("ERROR");
      } finally {
        setLoading(false);
      }
    };

    fetchMeeting();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/fm_zoom_register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, coupon }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }

      // Success = the Zoom registrant object (no {code} gate reason, no {error}).
      if (data && !data.error && !data.code) {
        setSubmitted(true);
      } else if (data && data.code === 409 && data.already_registered) {
        // Already registered for this meeting — link re-sent to their email.
        setAlreadyRegistered(true);
        setSubmitted(true);
      } else if (data && data.code === 402) {
        // Paid webinar, no valid coupon → send them to pay. On return, the
        // payment-success page registers them to Zoom by txnid.
        await startPayment();
      } else if (data && data.code === 400) {
        setError(data.message || "That coupon is not valid.");
      } else {
        console.error("❌ Registration error:", data);
        setError("Registration failed. Please try again later.");
      }
    } catch (err) {
      console.error("❌ Registration error:", err);
      setError("Registration failed. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Paid registration → create a payment and redirect to the gateway (full page).
  const startPayment = async () => {
    try {
      const res = await fetch(`${PAY_API}/fm_payment_create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_type: "zoom_registration",
          channel: "web",
          customer: {
            name: `${form.first_name} ${form.last_name}`.trim(),
            email: form.email,
            phone: form.phone,
          },
        }),
      });
      const data = await res.json();
      if (data && data.code === 200 && data.checkout) {
        if (data.checkout.mode === "form_post") {
          // PayU — build + submit the hidden form (whole page goes to PayU).
          const { action, fields } = data.checkout;
          const f = document.createElement("form");
          f.method = "POST";
          f.action = action;
          Object.entries(fields || {}).forEach(([k, v]) => {
            const i = document.createElement("input");
            i.type = "hidden";
            i.name = k;
            i.value = v;
            f.appendChild(i);
          });
          document.body.appendChild(f);
          f.submit();
          return; // navigating away to the gateway
        }
        // Cashfree (or any non-form provider) — server checkout launch page.
        window.location.href = `${PAY_API}/fm_payment_checkout?txnid=${data.txnid}`;
        return;
      }
      setError(
        (data && data.message) || "Could not start payment. Please try again."
      );
    } catch (err) {
      console.error("payment start error", err);
      setError("Could not start payment. Please try again.");
    }
  };

  const handleReset = () => {
    setForm({ first_name: "", last_name: "", email: "", phone: "" });
    setCoupon("");
    setSubmitted(false);
    setAlreadyRegistered(false);
    setError("");
  };

  if (loading && !submitted && !meeting)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(90deg, #c3f0fc, #e0d4fc)",
        }}
      >
        <p style={{ color: "#555", fontSize: 18 }}>Loading meeting details...</p>
      </div>
    );

  if (meeting === "NO_MEETING" || meeting === "ERROR")
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(90deg, #c3f0fc, #e0d4fc)",
          textAlign: "center",
          padding: 20,
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: 40,
            borderRadius: 20,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            maxWidth: 500,
          }}
        >
          <h1 style={{ fontSize: 28, color: "#ff6b6b", marginBottom: 20 }}>
            ⚠️ Error
          </h1>
          <p style={{ fontSize: 18, color: "#555", lineHeight: 1.5 }}>
            Meeting details not found. Please contact the organizer.
          </p>
        </div>
      </div>
    );

  // ✅ Success Page
  if (submitted && meeting)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(90deg, #c3f0fc, #e0d4fc)",
          padding: 20,
          textAlign: "center",
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: 40,
            borderRadius: 20,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            maxWidth: 500,
          }}
        >
          <h1 style={{ fontSize: 32, color: "#28a745", marginBottom: 20 }}>
            {alreadyRegistered ? "✅ You're already registered" : "✅ Congratulations!"}
          </h1>
          <p style={{ fontSize: 18, color: "#555", lineHeight: 1.5 }}>
            {alreadyRegistered
              ? "This email is already registered for this webinar — we've re-sent your join link to your email."
              : "You are now registered for the upcoming webinar"}
          </p>
          <p style={{ fontSize: 16, color: "#555", marginTop: 20 }}>
            <strong>Topic:</strong> "{meeting.topic}"
            <br />
            <strong>Time:</strong>{" "}
            {new Date(meeting.start_time).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
            <br />
            <strong>Duration:</strong> {meeting.duration || "N/A"} mins
            <br />
            <strong>Host:</strong> {meeting?.settings?.contact_name || "N/A"}
          </p>
          <p style={{ fontSize: 16, color: "#555", marginTop: 20 }}>
            Information relating to joining the webinar has been sent to your email.
          </p>
        </div>
      </div>
    );

  // 📝 Registration Form
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: 20,
        background: "linear-gradient(90deg, #c3f0fc, #e0d4fc)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
          width: "100%",
          maxWidth: 600,
        }}
      >
        {/* Meeting Info */}
        <div
          style={{
            background: "#fff",
            padding: 30,
            borderRadius: 20,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: "#111",
              marginBottom: 20,
            }}
          >
            {meeting?.topic}
          </h1>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                background: "#d0ebff",
                color: "#1c7ed6",
                padding: "6px 12px",
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              🗓️{" "}
              {new Date(meeting?.start_time).toLocaleString("en-US", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </div>
            <div
              style={{
                background: "#d3f9d8",
                color: "#37b24d",
                padding: "6px 12px",
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              ⏱ {meeting?.duration || "N/A"} min
            </div>
            <div
              style={{
                background: "#ffe8cc",
                color: "#d9480f",
                padding: "6px 12px",
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              👤 Host: {meeting?.settings?.contact_name || "N/A"}
            </div>
          </div>
          <p style={{ color: "#555", fontSize: 16, lineHeight: 1.5 }}>
            Fill in your details below to register for this webinar.
          </p>

          {/* Price */}
          {price && (
            <div style={{ marginTop: 8, fontSize: 20 }}>
              {price.is_free ? (
                <span style={{ color: "#37b24d", fontWeight: 700 }}>
                  🎟️ This webinar is Free
                </span>
              ) : price.discount != null && price.discount < price.actual ? (
                <span>
                  <span style={{ fontWeight: 700, color: "#111" }}>
                    ₹{price.discount}
                  </span>{" "}
                  <s style={{ color: "#999", fontSize: 16 }}>₹{price.actual}</s>
                </span>
              ) : (
                <span style={{ fontWeight: 700, color: "#111" }}>
                  ₹{price.actual}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Registration Form */}
        <div
          style={{
            background: "#fff",
            padding: 30,
            borderRadius: 20,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <h2
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: "#111",
              marginBottom: 20,
            }}
          >
            Register
          </h2>
          <form
            onSubmit={handleSubmit}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 15,
            }}
          >
            {["first_name", "last_name", "email", "phone"].map((field) => (
              <div key={field} style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", marginBottom: 5 }}>
                  {field.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                </label>
                <input
                  type={field === "email" ? "email" : "text"}
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 8,
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            ))}

            {/* Coupon (only when the organizer has an active coupon) */}
            {price && price.coupon_enabled && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", marginBottom: 5 }}>
                  Have a coupon? (optional)
                </label>
                <input
                  type="text"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  disabled={loading}
                  placeholder="Enter coupon code to join free"
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 8,
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            )}

            {error && (
              <p
                style={{
                  gridColumn: "1 / -1",
                  color: "red",
                  fontSize: 14,
                  marginTop: 5,
                }}
              >
                {error}
              </p>
            )}

            <div
              style={{
                gridColumn: "1 / -1",
                display: "flex",
                gap: 15,
              }}
            >
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: 14,
                  borderRadius: 12,
                  background: "linear-gradient(90deg,#4f46e5,#6366f1)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Processing..." : "Register"}
              </button>

              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: 14,
                  borderRadius: 12,
                  background: "#ccc",
                  color: "#333",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
